// src/components/visual/EmberCanvas.tsx
import React, { useEffect, useRef } from 'react';

interface EmberParticle {
  x: number;
  y: number;
  size: number;
  speedY: number;
  swaySpeed: number;
  swayAmplitude: number;
  swayOffset: number;
  opacity: number;
  maxOpacity: number;
  colorPrefix: string;
}

export const EmberCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Respect reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let rafId: number | null = null;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const isMobile = width < 768;
    const count = isMobile ? 20 : 45;

    const colorPalette = [
      'rgba(255, 72, 20, ',   // Volcanic ember flare
      'rgba(230, 197, 110, ', // Antique gold spark
      'rgba(179, 38, 8, ',    // Deep magma
    ];

    // Pre-allocate fixed particles array (zero GC in render loop)
    const particles: EmberParticle[] = [];
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: Math.random() * 2.0 + 0.8,
        speedY: Math.random() * 0.4 + 0.2,
        swaySpeed: Math.random() * 0.012 + 0.004,
        swayAmplitude: Math.random() * 20 + 8,
        swayOffset: Math.random() * Math.PI * 2,
        opacity: 0,
        maxOpacity: Math.random() * 0.6 + 0.2,
        colorPrefix: colorPalette[Math.floor(Math.random() * colorPalette.length)],
      });
    }

    let tick = 0;
    let isVisible = !document.hidden;

    // Render loop
    const render = () => {
      if (!isVisible) return;
      tick++;

      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < count; i++) {
        const p = particles[i];
        p.y -= p.speedY;
        const currentX = p.x + Math.sin(tick * p.swaySpeed + p.swayOffset) * p.swayAmplitude;

        // Smooth opacity ramp along vertical rise
        const progress = 1 - p.y / height;
        if (progress < 0.15) {
          p.opacity = (progress / 0.15) * p.maxOpacity;
        } else if (progress > 0.85) {
          p.opacity = ((1 - progress) / 0.15) * p.maxOpacity;
        } else {
          p.opacity = p.maxOpacity;
        }

        ctx.fillStyle = `${p.colorPrefix}${Math.max(0, p.opacity).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(currentX, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Recycle particle once it drifts off the top
        if (p.y < -10) {
          p.y = height + 10;
          p.x = Math.random() * width;
        }
      }

      rafId = requestAnimationFrame(render);
    };

    // Visibility listener to halt loop when user switches tabs
    const handleVisibility = () => {
      isVisible = !document.hidden;
      if (isVisible) {
        if (!rafId) {
          rafId = requestAnimationFrame(render);
        }
      } else {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Resize handler
    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize, { passive: true });

    rafId = requestAnimationFrame(render);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 -z-10 h-full w-full opacity-60"
      aria-hidden="true"
    />
  );
};
